import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { getAllClaims, getClaimsByStatus, clearClaimMessage, selectClaims, selectClaimsLoading, selectClaimsError, selectClaimsMessage, selectPagination } from '../features/claims/claimSlice';
import { selectCurrentUser, selectUserRoles, selectHasPermission } from '../features/auth/authSlice';
import { getDocumentsByClaimId } from '../features/documents/documentSlice';
import { useToast } from '../contexts/ToastContext';
import { useListFilters } from '../hooks/useListFilters';
import { apiClient } from '../utils/apiClient';
import { BUSINESS_CLAIM_TYPES } from '../constants/businessClaimTypes';

// Import the extracted components
import ClaimsListHeader from '../components/ClaimsList/ClaimsListHeader';
import ClaimsFilters from '../components/ClaimsList/ClaimsFilters';
import ErrorMessage from '../components/ClaimsList/ErrorMessage';
import ClaimsTable from '../components/ClaimsList/ClaimsTable';

import ClaimsSummaryStats from '../components/ClaimsList/ClaimsSummaryStats';
import LoadingState from '../components/ClaimsList/LoadingState';

import BackButton from '../components/ui/BackButton';
import OmPagination from '../components/ui/OmPagination';

// Add formatCurrency utility function
const formatCurrency = (amount) => {
  if (!amount) return 'R 0.00';
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'GHS',
    minimumFractionDigits: 2
  }).format(amount);
};

const ClaimsList = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const claims = useSelector(selectClaims);
  const isLoading = useSelector(selectClaimsLoading);
  const isError = useSelector(selectClaimsError);
  const message = useSelector(selectClaimsMessage);
  const pagination = useSelector(selectPagination);
  const user = useSelector(selectCurrentUser, shallowEqual);
  const userRoles = useSelector(selectUserRoles, shallowEqual);

  // Get initial status filter from URL parameters
  const getInitialStatusFilter = () => {
    const searchParams = new URLSearchParams(location.search);
    const statusParam = searchParams.get('status');
    return statusParam || 'all';
  };

  // Always get status filter from URL parameters
  const getStatusFilterFromUrl = () => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get('status') || 'all';
  };

  // Get initial SLA status filter from URL parameters
  const getInitialSlaStatusFilter = () => {
    const searchParams = new URLSearchParams(location.search);
    const slaStatusParam = searchParams.get('slaStatus');
    return slaStatusParam || 'all';
  };

  // Get assigned to me flag from URL parameters
  const getAssignedToMeFlag = () => {
    const searchParams = new URLSearchParams(location.search);
    const assignedToMeParam = searchParams.get('assignedToMe');
    return assignedToMeParam === 'true';
  };

  // Initialize filter states manually to avoid initialization issues
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');

  // Use status filter directly from URL
  const statusFilter = getStatusFilterFromUrl();
  
  // Create setter function for status filter that updates URL
  const setStatusFilter = (newStatus) => {
    const searchParams = new URLSearchParams(location.search);
    if (newStatus === 'all') {
      searchParams.delete('status');
    } else {
      searchParams.set('status', newStatus);
    }
    const newSearch = searchParams.toString();
    const newPath = newSearch ? `${location.pathname}?${newSearch}` : location.pathname;
    navigate(newPath, { replace: true });
  };

  // Initialize state variables first
  const [isExporting, setIsExporting] = useState(false);
  const [slaStatusFilter, setSlaStatusFilter] = useState(getInitialSlaStatusFilter());
  const [assignedToMe] = useState(getAssignedToMeFlag());
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  // Apply all filters in one place
  const getFilteredClaims = () => {
    if (!Array.isArray(claims)) return [];
    
    let filtered = [...claims];
    
    // Debug logging for assignment filtering
    if (assignedToMe && user?.userId) {
      console.log('🔍 [ClaimsList] My Claims Filter Debug:', {
        currentUserId: user.userId,
        assignedToMe: assignedToMe,
        totalClaims: filtered.length,
        claimsWithAssignment: filtered.filter(c => c.assignedTo?.id || c.assignedToId).map(c => ({
          id: c.id,
          assignedTo: c.assignedTo,
          assignedToId: c.assignedToId
        }))
      });
    }
    
    // Apply status filter if not 'all'
    if (statusFilter && statusFilter !== 'all') {
      // Handle comma-separated statuses (e.g., "RECOMMENDED_FOR_APPROVAL,BENEFIT_PENDING_APPROVAL")
      const allowedStatuses = statusFilter.includes(',') 
        ? statusFilter.split(',').map(s => s.trim())
        : [statusFilter];
      
      filtered = filtered.filter(claim => allowedStatuses.includes(claim.status));
    }
    
    // Apply SLA status filter
    if (slaStatusFilter !== 'all' && slaStatusFilter) {
      filtered = filtered.filter(claim => {
        if (!claim.sla) return false;
        
        switch (slaStatusFilter) {
          case 'AT_RISK':
            return claim.sla.slaStatus === 'AT_RISK';
          case 'WITHIN_SLA':
            return claim.sla.slaStatus === 'WITHIN_SLA';
          case 'BREACHED':
            return claim.sla.slaStatus === 'BREACHED';
          default:
            return true;
        }
      });
    }
    
    // Apply assignedToMe filter - show only claims assigned to current user
    if (assignedToMe && user?.userId) {
      filtered = filtered.filter(claim => {
        // Handle both assignedTo object format and assignedToId direct format
        const assignedToId = claim.assignedTo?.id || claim.assignedToId;
        return assignedToId === user.userId;
      });
    }
    
    // Apply search filter (client-side)
    if (searchTerm && searchTerm.trim().length > 0) {
      const term = searchTerm.trim().toLowerCase();
      filtered = filtered.filter((claim) => {
        const idStr = (claim.id !== undefined && claim.id !== null) ? String(claim.id).toLowerCase() : '';
        const policy = (claim.policyNumber || '').toLowerCase();
        const name = (claim.claimantName || '').toLowerCase();
        return (
          idStr.includes(term) ||
          policy.includes(term) ||
          name.includes(term)
        );
      });
    }

    // Apply sort (client-side)
    const compare = (a, b) => {
      switch (sortBy) {
        case 'createdAt': {
          const av = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const bv = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return sortOrder === 'asc' ? av - bv : bv - av; // desc = newest first
        }
        case 'updatedAt': {
          const av = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
          const bv = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
          return sortOrder === 'asc' ? av - bv : bv - av;
        }
        case 'benefitAmount': {
          const av = typeof a.benefitAmount === 'number' ? a.benefitAmount : (parseFloat(a.benefitAmount) || 0);
          const bv = typeof b.benefitAmount === 'number' ? b.benefitAmount : (parseFloat(b.benefitAmount) || 0);
          return sortOrder === 'asc' ? av - bv : bv - av;
        }
        case 'status': {
          const av = (a.status || '').toString().toLowerCase();
          const bv = (b.status || '').toString().toLowerCase();
          if (av === bv) return 0;
          const base = av < bv ? -1 : 1;
          return sortOrder === 'asc' ? base : -base;
        }
        default: {
          const av = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const bv = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return sortOrder === 'asc' ? av - bv : bv - av;
        }
      }
    };

    filtered.sort(compare);

    return filtered;
  };

  const filteredClaims = getFilteredClaims();

  // Strict Claims Officer role validation - only actual Claims Officers can perform assignment actions
  const isClaimsOfficer = userRoles.includes('Claims_Officer');

  // Check if user has export permission
  const canExport = useSelector(state => selectHasPermission(state, 'claims:export'));

  // Handle CSV export
  const handleExportCSV = async () => {
    try {
      setIsExporting(true);
      
      // Build query parameters for export based on current filters
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      if (slaStatusFilter && slaStatusFilter !== 'all') {
        params.append('slaStatus', slaStatusFilter);
      }
      
      const response = await apiClient.get(`/claims/export?${params.toString()}`, {
        responseType: 'blob',
      });

      // Create blob and download
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename with current date
      const timestamp = new Date().toISOString().split('T')[0];
      link.download = `claims_export_${timestamp}.csv`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      showSuccess('Claims exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      showError(error.response?.data?.error || 'Failed to export claims');
    } finally {
      setIsExporting(false);
    }
  };

  useEffect(() => {
    dispatch(clearClaimMessage());
    
    // Handle navigation state message (from successful claim submission)
    if (location.state?.message) {
      showSuccess(location.state.message);
      // Clear the navigation state to prevent showing the message again on refresh
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
    
    const paginationOptions = {
      page: currentPage,
      limit: itemsPerPage,
      forceRefresh: true // Always force refresh to avoid 304 issues on initial load
    };
    
    // Route-aware filtering - apply different filters based on current route
    const currentPath = location.pathname;
    let apiOptions = { ...paginationOptions };
    
    if (currentPath === '/claims/my-claims') {
      // User's own submitted claims
      apiOptions.submittedBy = user?.userId;
    } else if (currentPath === '/claims/process') {
      // Claims for Claims Officers to process - either assigned to them or newly submitted
      if (statusFilter === 'all' || !statusFilter) {
        apiOptions.status = 'SUBMITTED';
      }
      // Could also add: apiOptions.assignedTo = user?.userId; if you want only assigned claims
    } else if (currentPath === '/claims/approve') {
      // Claims awaiting approval for Approvers
      if (statusFilter === 'all' || !statusFilter) {
        apiOptions.status = 'RECOMMENDED_FOR_APPROVAL';
      }
    } else if (currentPath === '/claims/payments') {
      // Approved claims for Finance officers
      if (statusFilter === 'all' || !statusFilter) {
        apiOptions.status = 'APPROVED';
      }
    }
    // For '/claims' (admin route), no additional filters - show all claims
    
    // Always use the status from the URL for API calls
    if (statusFilter === 'all') {
      dispatch(getAllClaims(apiOptions));
    } else {
      dispatch(getClaimsByStatus({ status: statusFilter, ...paginationOptions, submittedBy: apiOptions.submittedBy, assignedTo: apiOptions.assignedTo }));
    }
  }, [dispatch, statusFilter, currentPage, itemsPerPage, showSuccess, location.pathname, user?.userId]);

  // Add a manual refresh function
  const handleRefresh = (forceRefresh = true) => {
    const paginationOptions = {
      page: currentPage,
      limit: itemsPerPage,
      forceRefresh: forceRefresh // Force fresh data to bypass cache
    };
    
    // Route-aware filtering - apply different filters based on current route
    const currentPath = location.pathname;
    let apiOptions = { ...paginationOptions };
    
    if (currentPath === '/claims/my-claims') {
      // User's own submitted claims
      apiOptions.submittedBy = user?.userId;
    } else if (currentPath === '/claims/process') {
      // Claims for Claims Officers to process
      if (statusFilter === 'all' || !statusFilter) {
        apiOptions.status = 'SUBMITTED';
      }
    } else if (currentPath === '/claims/approve') {
      // Claims awaiting approval for Approvers
      if (statusFilter === 'all' || !statusFilter) {
        apiOptions.status = 'RECOMMENDED_FOR_APPROVAL';
      }
    } else if (currentPath === '/claims/payments') {
      // Approved claims for Finance officers
      if (statusFilter === 'all' || !statusFilter) {
        apiOptions.status = 'APPROVED';
      }
    }
    
    // Use getClaimsByStatus if we have a specific status, otherwise getAllClaims
    if (apiOptions.status && statusFilter === 'all') {
      dispatch(getClaimsByStatus({ status: apiOptions.status, ...paginationOptions, submittedBy: apiOptions.submittedBy, assignedTo: apiOptions.assignedTo }));
    } else if (statusFilter === 'all') {
      dispatch(getAllClaims(apiOptions));
    } else {
      dispatch(getClaimsByStatus({ status: statusFilter, ...paginationOptions, submittedBy: apiOptions.submittedBy, assignedTo: apiOptions.assignedTo }));
    }
  };



  // Handle page change for pagination
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const getStatusBadgeColor = (status) => {
    switch (status?.toUpperCase()) {
      case 'DRAFT':
        return 'bg-steel/10 text-steel border border-steel/20';
      case 'SUBMITTED':
        return 'bg-fresh-leaf/10 text-fresh-leaf border border-fresh-leaf/20';
      case 'UNDER_REVIEW':
        return 'bg-zesty-lime/10 text-woodland-moss border border-zesty-lime/20';
      case 'APPROVED':
        return 'bg-forest-fern/10 text-forest-fern border border-forest-fern/20';
      case 'REJECTED':
        return 'bg-red-500/10 text-red-700 border border-red-500/20';
      case 'MISSING_DOCUMENTS':
        return 'bg-orange-500/10 text-orange-700 border border-orange-500/20';
      case 'PAYMENT_PENDING':
        return 'bg-deep-jungle/10 text-deep-jungle border border-deep-jungle/20';
      case 'PAID':
        return 'bg-forest-fern/15 text-forest-fern border border-forest-fern/30';
      case 'CLOSED':
        return 'bg-graphite/10 text-graphite border border-graphite/20';
      default:
        return 'bg-steel/10 text-steel border border-steel/20';
    }
  };

  const getClaimTypeLabel = (type) => {
    // First try business claim types
    if (BUSINESS_CLAIM_TYPES[type]?.displayName) {
      return BUSINESS_CLAIM_TYPES[type].displayName;
    }
    
    // Fallback for enum values from backend
    const enumLabels = {
      'DEATH': 'Death Claim',
      'DISABILITY': 'Disability Claim', 
      'CRITICAL_ILLNESS': 'Critical Illness',
      'SPOUSAL_COVER': 'Spousal Cover',
      'COFFIN_COVER': 'Coffin Cover',
      'PARENT_COVER': 'Parent Cover',
      'MEDICAL': 'Medical Claim'
    };
    
    return enumLabels[type] || type || 'Unknown';
  };
  
  // Check if user should see "My Claims" view
  const isMyClaimsView = userRoles.some(role => ['HR', 'Broker', 'CRM'].includes(role)) && 
                        !userRoles.some(role => ['Claims_Officer', 'Approver', 'Finance', 'Admin'].includes(role));

  if (isLoading) {
    return <LoadingState />;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Back Button */}
        <div className="mb-6">
          <BackButton />
        </div>

        <ClaimsListHeader
          isMyClaimsView={isMyClaimsView}
          claims={claims}
          filteredClaims={filteredClaims}
          user={user}
          userRoles={userRoles}
          isLoading={isLoading}
          onRefresh={handleRefresh}
          canExport={canExport}
          onExportCSV={handleExportCSV}
          isExporting={isExporting}
          summaryStatsComponent={<ClaimsSummaryStats filteredClaims={filteredClaims} formatCurrency={formatCurrency} />}
        />

        <ClaimsFilters
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          sortBy={sortBy}
          setSortBy={setSortBy}
          sortOrder={sortOrder}
          setSortOrder={setSortOrder}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
        />

        <ErrorMessage isError={isError} message={message} />

        <ClaimsTable
          filteredClaims={filteredClaims}
          isClaimsOfficer={isClaimsOfficer}
          getStatusBadgeColor={getStatusBadgeColor}
          getClaimTypeLabel={getClaimTypeLabel}
          user={user}
        />

        {/* Pagination Component */}
        {!isLoading && !isError && pagination?.total > 0 && (
          <OmPagination
            currentPage={currentPage}
            totalItems={pagination.total}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            showResultsInfo={true}
          />
        )}
      </div>
    </div>
  );
};

export default ClaimsList;

